import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Segmented, message } from 'antd'
import {
  CheckCircleFilled,
  CloudDownloadOutlined,
  DiffOutlined,
} from '@ant-design/icons'
import { exportCompareReport } from '../api/compare-data-api'
import {
  getIdeonCompareRows,
  type IdeonCompareResult,
  type IdeonCompareRow,
  type IdeonCompareRowStatus,
} from '../api/ideon-compare-api'
import {
  SURECO_CSV_DATA_TYPE_OPTIONS,
  dataTypeLabel,
  type SurecoCsvDataType,
} from '../constants'
import { getColumnLabel } from '../columnLabels'
import { formatHeaderLabel } from '../utils/compareDataHelpers'
import {
  countChanges,
  countsFor,
  formatCompletedAt,
  formatVersionDate,
  isSurecoCsvDataType,
  skippedRowsMessage,
} from '../utils/ideonCompareHelpers'
import {
  IdeonDiffCellRenderer,
  getIdeonCellValue,
  isIdeonFieldModified,
} from './IdeonDiffCellRenderer'
import {
  CompareResultsGrid,
  type CompareGridPageRequest,
} from './CompareResultsGrid'
import {
  CompareFilterBar,
  CompareResultsToolbar,
  canSearchHios,
} from './CompareFilterBar'
import { COMPARE_STATUSES, compareStatusLabel } from './StatusBadge'
import SummaryCard from './SummaryCard'
import styles from '../PlanManagementV3.module.scss'

const STATUS_FILTERS: {
  status: IdeonCompareRowStatus
  countKey: 'addedCount' | 'removedCount' | 'modifiedCount'
}[] = [
  { status: 'ADDED', countKey: 'addedCount' },
  { status: 'REMOVED', countKey: 'removedCount' },
  { status: 'MODIFIED', countKey: 'modifiedCount' },
]

// Grid callbacks that don't depend on props — module-level so they're stable.
const ideonFieldPath = (key: string) => `fieldCompare.${key}.targetValue`
const ideonCellClassRules = (key: string) => ({
  [styles.diffCellHighlight]: (p: { data?: IdeonCompareRow }) =>
    !!p.data && isIdeonFieldModified(p.data, key),
})
const renderIdeonCell = (row: IdeonCompareRow, key: string) => (
  <IdeonDiffCellRenderer row={row} fieldKey={key} />
)

function DataTypeLabel({ label, changes }: { label: string; changes: number }) {
  return (
    <span className={styles.ideonTypeOption}>
      {label}
      {changes > 0 ? (
        <span className={styles.ideonTypeCount}>
          {changes.toLocaleString()}
        </span>
      ) : (
        <CheckCircleFilled
          className={styles.ideonTypeNoChanges}
          aria-label="No changes"
        />
      )}
    </span>
  )
}

export function IdeonCompareResults({
  result,
  dataType,
  onDataTypeChange,
  states,
  onStatesChange,
  hiosSearch,
  onHiosSearchChange,
}: {
  result: IdeonCompareResult
  dataType: SurecoCsvDataType
  onDataTypeChange: (dataType: SurecoCsvDataType) => void
  /** Empty = all states. */
  states: string[]
  onStatesChange: (states: string[]) => void
  /** HIOS plan / issuer ID prefix; '' = no search. */
  hiosSearch: string
  onHiosSearchChange: (search: string) => void
}) {
  const [statusFilter, setStatusFilter] =
    useState<IdeonCompareRowStatus | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  // Total rows of the grid's current query — the only count that reflects
  // the HIOS search (the run's summary counts are per state only).
  const [gridTotal, setGridTotal] = useState<number | null>(null)
  const search = canSearchHios(dataType) ? hiosSearch : ''

  // Every run covers all states with per-state counts, so the state filter
  // is just arithmetic here plus a WHERE on the rows — never a new run.
  const counts = countsFor(result, dataType, states)
  const changes = countChanges(counts)
  const skippedMessage = skippedRowsMessage(
    result,
    dataType,
    states,
    (key) => getColumnLabel(dataType, key) ?? formatHeaderLabel(key)
  )
  const visibleCount = statusFilter
    ? counts[
        STATUS_FILTERS.find((f) => f.status === statusFilter)?.countKey ??
          'modifiedCount'
      ]
    : changes

  const fetchPage = useCallback(
    async (request: CompareGridPageRequest) => {
      const page = await getIdeonCompareRows({
        ...request,
        runId: result.runId,
        dataType,
        status: statusFilter ?? undefined,
        states,
        hiosSearch: search || undefined,
      })
      setGridTotal(page.total)
      return page
    },
    [result.runId, dataType, statusFilter, states, search]
  )
  const gridKey = `${result.runId}:${dataType}:${statusFilter ?? 'all'}:${states.join(',')}:${search}`
  useEffect(() => setGridTotal(null), [gridKey])

  const handleDataTypeChange = (next: SurecoCsvDataType) => {
    setStatusFilter(null)
    onDataTypeChange(next)
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      await exportCompareReport({
        runId: result.runId,
        dataType,
        compareStatus: statusFilter ?? undefined,
        states,
        hiosSearch: search || undefined,
      })
    } catch (err) {
      console.error('Failed to download compare report', err)
      message.error('Failed to download the report.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className={styles.ideonResults}>
      <div className={styles.ideonResultMeta}>
        <span className={styles.metaLabel}>
          <strong>PY {result.planYear}</strong>
        </span>
        <span className={styles.ideonResultVersions}>
          {formatVersionDate(result.baseVersion)}
          <span className={styles.ideonVersionMeta}>
            ({result.baseVersion.fetchedBy})
          </span>
          <span className={styles.ideonArrow}>→</span>
          {formatVersionDate(result.targetVersion)}
          <span className={styles.ideonVersionMeta}>
            ({result.targetVersion.fetchedBy})
          </span>
        </span>
        <span className={styles.ideonVersionMeta}>
          Compared {formatCompletedAt(result.completedAt)}
        </span>
      </div>

      <CompareFilterBar
        stateCodes={result.summary.states}
        states={states}
        onStatesChange={(next) => {
          setStatusFilter(null)
          onStatesChange(next)
        }}
        hiosSearch={search}
        onHiosSearchChange={onHiosSearchChange}
        showHiosSearch={canSearchHios(dataType)}
      />

      {skippedMessage && (
        <Alert
          type="warning"
          showIcon
          className={styles.compareAlert}
          message={skippedMessage}
        />
      )}

      <div className={styles.ideonTypeSwitcher}>
        <Segmented
          value={dataType}
          onChange={(value) => {
            if (isSurecoCsvDataType(value)) handleDataTypeChange(value)
          }}
          options={SURECO_CSV_DATA_TYPE_OPTIONS.map(({ label, value }) => ({
            value,
            label: (
              <DataTypeLabel
                label={label}
                changes={countChanges(countsFor(result, value, states))}
              />
            ),
          }))}
        />
      </div>

      <div className={styles.summaryRow}>
        {STATUS_FILTERS.map((f) => (
          <SummaryCard
            key={f.status}
            label={compareStatusLabel(f.status)}
            value={counts[f.countKey]}
            accentColor={COMPARE_STATUSES[f.status]?.color}
            active={statusFilter === f.status}
            onClick={() =>
              setStatusFilter((prev) => (prev === f.status ? null : f.status))
            }
          />
        ))}
        <SummaryCard label="Unchanged" value={counts.unchangedCount} />
        <SummaryCard label="Total" value={counts.total} />
      </div>

      {changes === 0 ? (
        <div className={styles.ideonNoDiff}>
          <div className={styles.emptyStateIcon}>
            <DiffOutlined />
          </div>
          <h2 className={styles.emptyStateTitle}>No differences found</h2>
          <p className={styles.emptyStateDescription}>
            {dataTypeLabel(dataType)} is identical in both versions
            {states.length > 0 ? ' for the selected states' : ''}.
          </p>
        </div>
      ) : (
        <>
          <CompareResultsToolbar
            statusLabel={statusFilter ? compareStatusLabel(statusFilter) : null}
            count={search ? gridTotal : visibleCount}
            onShowAll={() => setStatusFilter(null)}
          >
            <Button
              className={styles.dsButton}
              icon={<CloudDownloadOutlined />}
              loading={isDownloading}
              disabled={isDownloading}
              onClick={() => void handleDownload()}
            >
              Download report
            </Button>
          </CompareResultsToolbar>
          <CompareResultsGrid
            key={gridKey}
            dataType={dataType}
            fetchPage={fetchPage}
            fieldPath={ideonFieldPath}
            cellText={getIdeonCellValue}
            cellClassRules={ideonCellClassRules}
            renderCell={renderIdeonCell}
          />
        </>
      )}
    </div>
  )
}
